import React from "react";

/**
 * 출력용 날짜 포맷
 * YYYYMMDD → YYYY. MM. DD
 */
export const formatPrintDate = (yyyymmdd?: string) => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return "";

  const yyyy = yyyymmdd.substring(0, 4);
  const mm = yyyymmdd.substring(4, 6);
  const dd = yyyymmdd.substring(6, 8);

  return `${yyyy}. ${mm}. ${dd}`;
};

/**
 * 금액 콤마 표시
 */
export const formatWon = (amt?: string | number) => {
  const num = Number(amt || 0);
  return num.toLocaleString("ko-KR");
};

/**
 * 숫자를 한글 금액으로 변환
 * 예: 1000000 → 일금 일백만원정
 */
const KOR_NUM = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];
const UNIT_SMALL = ["", "십", "백", "천"];
const UNIT_BIG = ["", "만", "억", "조", "경"];

export const toKoreanMoney = (value?: string | number) => {
  const num = Number(value || 0);

  if (!num) return "일금 영원정";

  const str = String(num);
  const groups: string[] = [];
  let result = "";

  for (let i = str.length; i > 0; i -= 4) {
    groups.unshift(str.substring(Math.max(0, i - 4), i));
  }

  groups.forEach((group, groupIndex) => {
    let groupText = "";
    const digits = group.split("");
    const len = digits.length;

    digits.forEach((digit, idx) => {
      const n = Number(digit);
      if (n === 0) return;

      const unitIdx = len - idx - 1;
      groupText += `${KOR_NUM[n]}${UNIT_SMALL[unitIdx]}`;
    });

    const bigUnitIdx = groups.length - groupIndex - 1;

    if (groupText) {
      result += `${groupText}${UNIT_BIG[bigUnitIdx]}`;
    }
  });

  return `일금 ${result}원정`;
};

/**
 * 결재라인 1건
 */
interface AtrzLineItem {
  atrzLineId?: string;
  atrzId?: string;
  atrzSeq?: number | string;
  aprvrId?: string;
  atrzDt?: string;
  userNm?: string;
  atrzPrgrsSttsNm?: string;
  upOgnzCd?: string;
  ognzCd?: string;
  ognzNm?: string;
  upOgnzNm?: string;
  jbpsSeCd?: string;
  jbpsSeNm?: string; // 직위명
}

type RsdocType = "DPST" | "EXPND";

interface Props {
  title?: string;
  year?: string;
  actDt?: string;
  regDt?: string;
  gwan?: string;
  hang?: string;
  mok?: string;
  summary?: string;
  amount?: number | string;
  proofNo?: string;
  listLine?: AtrzLineItem[];
  rsdocType?: RsdocType;
}

type PrintCellItem =
  | { type: "APPR"; data: AtrzLineItem }
  | { type: "EMPTY" };

const BudgetRsdocPrint = ({
  title,
  year,
  actDt,
  regDt,
  gwan,
  hang,
  mok,
  summary,
  amount,
  proofNo,
  listLine = [],
  rsdocType = "EXPND"
}: Props) => {
  const H = {
    title: 25,
    top1: 13,
    top2: 14,
    top3: 14,

    bodyHead: 18,
    bodyRow: 38,

    apprTitle: 14,
    apprRole: 14,
    apprInfo: 32,

    apprProof: 14,
    apprProofBlank: 24,
  };

  const yearTypeText =
    year ? `${year}년도${rsdocType === "DPST" ? "수입" : "지출"}` : "";

  const OUTER_LINE = 2;
  const INNER_LINE = 1;
  const MAX_APPR_LINE = 4;

  const topTableHeight = H.title + H.top1 + H.top2 + H.top3;
  const bodyTotalHeight = H.bodyHead + H.bodyRow * 4;

  // 기존 결재라인 최대 3명
  const sortedLine = [...listLine]
    .sort((a, b) => Number(b.atrzSeq || 0) - Number(a.atrzSeq || 0))
    .slice(0, MAX_APPR_LINE);

  const fixedLineList: PrintCellItem[] = sortedLine.map((line) => ({
    type: "APPR" as const,
    data: line,
  }));

  while (fixedLineList.length < MAX_APPR_LINE) {
    fixedLineList.push({ type: "EMPTY" });
  }

  const apprTableHeight =
    H.apprTitle +
    MAX_APPR_LINE * (H.apprRole + H.apprInfo) +
    H.apprProof +
    H.apprProofBlank;

  const safeAmount = Number(amount || 0);
  const koreanMoney = toKoreanMoney(safeAmount);
  const moneyMiddleText = koreanMoney
    .replace(/^일금\s*/, "")
    .replace(/원정$/, "");

  const getCategoryFontSize = (text?: string) => {
    const len = (text || "").trim().length;

    if (len >= 12) return "16px";
    if (len >= 10) return "17px";
    return "19px";
  };

  const styles: Record<string, React.CSSProperties> = {
    page: {
      width: "190mm",
      height: "280mm",
      margin: "0 auto",
      paddingTop: "40mm",
      boxSizing: "border-box",
      background: "#fff",
      fontFamily: `"Batang","바탕",serif`,
      color: "#000",
      overflow: "hidden",
    },

    rowWrap: {
      display: "flex",
      alignItems: "flex-start",
      width: "100%",
    },

    left: {
      width: "82%",
      display: "flex",
      flexDirection: "column",
    },

    right: {
      width: "18%",
      marginLeft: `-${OUTER_LINE}px`,
    },

    topTable: {
      width: "100%",
      height: `${topTableHeight}mm`,
      borderCollapse: "collapse",
      tableLayout: "fixed",
      background: "#fff",
      border: `${OUTER_LINE}px solid #000`,
      borderBottom: `${OUTER_LINE}px solid #000`,
      boxSizing: "border-box",
    },

    apprTable: {
      width: "100%",
      height: `${apprTableHeight}mm`,
      borderCollapse: "collapse",
      tableLayout: "fixed",
      background: "#fff",
      border: `${OUTER_LINE}px solid #000`,
      boxSizing: "border-box",
    },

    td: {
      border: `${INNER_LINE}px solid #000`,
      textAlign: "center",
      verticalAlign: "middle",
      fontWeight: 800,
      padding: 0,
      boxSizing: "border-box",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },

    titleText: {
      fontSize: "31px",
      letterSpacing: "10px",
      fontWeight: 900,
    },

    topText: {
      fontSize: "21px",
      letterSpacing: "2px",
      fontWeight: 800,
    },

    labelText: {
      fontSize: "19px",
      letterSpacing: "1px",
      fontWeight: 800,
    },

    valueText: {
      fontSize: "19px",
      letterSpacing: "1px",
      fontWeight: 800,
    },

    categoryText: {
      fontSize: "19px",
      fontWeight: 800,
      letterSpacing: "1px",
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    smallText: {
      fontSize: "18px",
      letterSpacing: "0px",
      fontWeight: 800,
    },

    bodyWrap: {
      width: "100%",
      height: `${bodyTotalHeight}mm`,
      display: "grid",
      gridTemplateColumns: "40% 45% 15%",
      gridTemplateRows: `${H.bodyHead}mm ${H.bodyRow}mm ${H.bodyRow}mm ${H.bodyRow}mm ${H.bodyRow}mm`,
      background: "#fff",
      boxSizing: "border-box",
      borderLeft: `${OUTER_LINE}px solid #000`,
      borderRight: `${OUTER_LINE}px solid #000`,
      borderBottom: `${OUTER_LINE}px solid #000`,
      borderTop: "0",
    },

    cell: {
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      background: "#fff",
      borderRight: `${INNER_LINE}px solid #000`,
      borderBottom: `${INNER_LINE}px solid #000`,
    },

    cellLastCol: {
      borderRight: "0",
    },

    cellLastRow: {
      borderBottom: `${OUTER_LINE}px solid #000`,
    },

    headCellText: {
      fontSize: "21px",
      letterSpacing: "8px",
      fontWeight: 900,
    },

    summaryText: {
      width: "100%",
      fontSize: "20px",
      lineHeight: 1.35,
      fontWeight: 800,
      textAlign: "left",
      padding: "0 3mm 0 6mm",
      whiteSpace: "pre-line",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      boxSizing: "border-box",
    },

    amountText: {
      fontSize: "21px",
      fontWeight: 900,
      letterSpacing: "0px",
    },

    rightMergedCell: {
      gridColumn: "3 / 4",
      gridRow: "1 / 6",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fff",
      overflow: "hidden",
      padding: "4mm 0",
      borderLeft: `${OUTER_LINE}px solid #000`,
      borderBottom: `${OUTER_LINE}px solid #000`,
    },

    moneyVerticalWrap: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-between",
      boxSizing: "border-box",
    },

    moneyTopText: {
      writingMode: "vertical-rl",
      textOrientation: "upright",
      marginTop: "10px",
      fontSize: "28px",
      fontWeight: 900,
      lineHeight: 1,
      letterSpacing: "5px",
      flexShrink: 0,
    },

    moneyMiddleWrap: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%",
      minHeight: 0,
      paddingTop: "4mm",
      paddingBottom: "4mm",
      boxSizing: "border-box",
    },

    moneyAmountText: {
      writingMode: "vertical-rl",
      textOrientation: "upright",
      fontSize: "20px",
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: "1px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    },

    moneyDividerFlex: {
      width: "1px",
      flex: 1,
      backgroundColor: "#000",
      marginTop: "4mm",
      minHeight: "8mm",
    },

    moneyBottomText: {
      writingMode: "vertical-rl",
      textOrientation: "upright",
      marginBottom: "20px",
      fontSize: "28px",
      fontWeight: 900,
      lineHeight: 1,
      letterSpacing: "5px",
      flexShrink: 0,
    },

    apprInfoWrap: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "2mm 1mm",
      boxSizing: "border-box",
      whiteSpace: "normal",
      lineHeight: 1.2,
    },

    apprNameText: {
      fontSize: "16px",
      fontWeight: 800,
      letterSpacing: "0px",
      marginBottom: "2mm",
    },

    apprDateText: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0px",
    },

    proofText: {
      textAlign: "center",
      fontSize: "12px",
      fontWeight: 800,
      letterSpacing: "0px",
    },
  };

  const renderRoleText = (item: PrintCellItem) => {
    if (item.type === "APPR") {
      if (Number(item.data?.atrzSeq || 0) === 0) {
        return item.data?.jbpsSeNm || "결재상신자";
      }

      return item.data?.jbpsSeNm || "";
    }

    return "";
  };

  const renderApprovalInfo = (item: PrintCellItem) => {
    if (item.type === "APPR") {
      return (
        <div style={styles.apprInfoWrap}>
          <div style={styles.apprNameText}>{item.data?.userNm || ""}</div>
          <div style={styles.apprDateText}>{item.data?.atrzDt || ""}</div>
        </div>
      );
    } 
    return null;
  };

  return (
    <div style={styles.page}>
      <div style={styles.rowWrap}>
        <div style={styles.left}>
          <table style={styles.topTable}>
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "40%" }} />
            </colgroup>

            <tbody>
              <tr style={{ height: `${H.title}mm` }}>
                <td colSpan={4} style={styles.td}>
                  <div style={styles.titleText}>{title || ""}</div>
                </td>
              </tr>

              <tr style={{ height: `${H.top1}mm` }}>
                <td colSpan={2} style={{ ...styles.td, ...styles.topText }}>
                  {yearTypeText}
                </td>

                <td style={{ ...styles.td, ...styles.labelText }}>관</td>

                <td style={styles.td}>
                  <div
                    style={{
                      ...styles.categoryText,
                      fontSize: getCategoryFontSize(gwan),
                    }}
                  >
                    {gwan || ""}
                  </div>
                </td>
              </tr>

              <tr style={{ height: `${H.top2}mm` }}>
                <td style={{ ...styles.td, ...styles.labelText }}>
                  행위년월일
                </td>

                <td style={{ ...styles.td, ...styles.valueText }}>
                  {formatPrintDate(actDt)}
                </td>

                <td style={{ ...styles.td, ...styles.labelText }}>항</td>

                <td style={styles.td}>
                  <div
                    style={{
                      ...styles.categoryText,
                      fontSize: getCategoryFontSize(hang),
                    }}
                  >
                    {hang || ""}
                  </div>
                </td>
              </tr>

              <tr style={{ height: `${H.top3}mm` }}>
                <td style={{ ...styles.td, ...styles.labelText }}>
                  등기년월일
                </td>

                <td style={{ ...styles.td, ...styles.valueText }}>
                  {formatPrintDate(regDt)}
                </td>

                <td style={{ ...styles.td, ...styles.labelText }}>목</td>

                <td style={styles.td}>
                  <div
                    style={{
                      ...styles.categoryText,
                      fontSize: getCategoryFontSize(mok),
                    }}
                  >
                    {mok || ""}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div style={styles.bodyWrap}>
            <div style={styles.cell}>
              <div style={styles.headCellText}>적&nbsp;&nbsp;요</div>
            </div>

            <div style={{ ...styles.cell, ...styles.cellLastCol }}>
              <div style={styles.headCellText}>금&nbsp;&nbsp;&nbsp;액</div>
            </div>

            <div style={styles.rightMergedCell}>
              <div style={styles.moneyVerticalWrap}>
                <div style={styles.moneyTopText}>일금</div>

                <div style={styles.moneyMiddleWrap}>
                  <div style={styles.moneyAmountText}>{moneyMiddleText}</div>
                  <div style={styles.moneyDividerFlex} />
                </div>

                <div style={styles.moneyBottomText}>원정</div>
              </div>
            </div>

            <div style={styles.cell}>
              <div style={styles.summaryText}>{summary || ""}</div>
            </div>

            <div style={{ ...styles.cell, ...styles.cellLastCol }}>
              <div style={styles.amountText}>₩{formatWon(safeAmount)}</div>
            </div>

            <div style={styles.cell}></div>
            <div style={{ ...styles.cell, ...styles.cellLastCol }}></div>

            <div style={styles.cell}></div>
            <div style={{ ...styles.cell, ...styles.cellLastCol }}></div>

            <div style={{ ...styles.cell, ...styles.cellLastRow }}></div>
            <div
              style={{
                ...styles.cell,
                ...styles.cellLastCol,
                ...styles.cellLastRow,
              }}
            ></div>
          </div>
        </div>

        <div style={styles.right}>
          <table style={styles.apprTable}>
            <tbody>
              <tr style={{ height: `${H.apprTitle}mm` }}>
                <td style={{ ...styles.td, ...styles.smallText }}>
                  결&nbsp;&nbsp;재
                </td>
              </tr>

              {fixedLineList.map((item, idx) => (
                <React.Fragment key={idx}>
                  <tr style={{ height: `${H.apprRole}mm` }}>
                    <td style={{ ...styles.td, ...styles.smallText, fontSize: (renderRoleText(item).length >= 7 ? "15px" : "18px") }}>
                      {renderRoleText(item)}
                    </td>
                  </tr>

                  <tr style={{ height: `${H.apprInfo}mm` }}>
                    <td style={styles.td}>{renderApprovalInfo(item)}</td>
                  </tr>
                </React.Fragment>
              ))}

              <tr style={{ height: `${H.apprProof}mm` }}>
                <td style={{ ...styles.td, ...styles.smallText }}>증빙번호</td>
              </tr>

              <tr style={{ height: `${H.apprProofBlank}mm` }}>
                <td style={{ ...styles.td, ...styles.proofText }}>
                  {proofNo || ""}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetRsdocPrint;